import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiGet, apiDelete, operationsClient } from '@/lib/api';
import {
  opsEstimateEndpoints,
  opsProposalEndpoints,
  estimateTemplateEndpoints,
  proposalTemplateEndpoints,
} from '@/lib/api/endpoints';
import { useQueryClient } from '@tanstack/react-query';
import { estimateKeys, estimateTemplateKeys, proposalKeys } from '@/lib/queryKeys';
import { AttachmentDropzoneList } from '@/components/molecules/AttachmentDropzoneList';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { isRenderableImage } from '@/lib/attachments/attachment-files';
import type { Attachment } from '@/types/attachment';

export type { Attachment };

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/pdf',
  'video/mp4',
  'video/quicktime',
];

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

/**
 * Presigned-URL route per attachment, derived from the attachment's OWN
 * entityType/entityId rather than from this component's props.
 *
 * That distinction is the whole fix: in the create flow the form has no
 * estimate/proposal id yet, but every uploaded file already exists server-side
 * under the pending id (the upload-url call creates the row), and the
 * `:id/attachments/:attachmentId/download-url` routes resolve on the
 * attachment's own entityId — exactly how the delete path already addresses
 * pending rows. Template-copied previews (entityType `*_template`) resolve
 * against their template for the same reason.
 */
const DOWNLOAD_URL_BASE_BY_ENTITY_TYPE: Record<string, (entityId: string) => string> = {
  estimate: (id) => opsEstimateEndpoints.attachments(id),
  proposal: (id) => opsProposalEndpoints.attachments(id),
  estimate_template: (id) => estimateTemplateEndpoints.attachments(id),
  proposal_template: (id) => proposalTemplateEndpoints.attachments(id),
};

function downloadUrlPath(attachment: Attachment): string | null {
  const base = DOWNLOAD_URL_BASE_BY_ENTITY_TYPE[attachment.entityType];
  if (!base || !attachment.entityId) return null;
  return `${base(attachment.entityId)}/${attachment.id}/download-url`;
}

function isPlayableVideo(mimeType: string) {
  return mimeType === 'video/mp4' || mimeType === 'video/quicktime';
}

function isPdf(mimeType: string) {
  return mimeType === 'application/pdf';
}

/** Types the preview dialog can actually render in-app; the rest open in a tab. */
function isDialogPreviewable(mimeType: string) {
  return isRenderableImage(mimeType) || isPlayableVideo(mimeType) || isPdf(mimeType);
}

type BaseProps = {
  attachments: Attachment[];
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  isInternal?: boolean;
  readOnly?: boolean;
};

// Existing estimate: pass estimateId
// New estimate (no ID yet): pass pendingId
// Estimate template: pass templateId
type EstimateAttachmentsProps =
  | (BaseProps & {
      estimateId: string;
      pendingId?: never;
      proposalId?: never;
      proposalPendingId?: never;
      templateId?: never;
    })
  | (BaseProps & {
      estimateId?: never;
      pendingId: string;
      proposalId?: never;
      proposalPendingId?: never;
      templateId?: never;
    })
  | (BaseProps & {
      estimateId?: never;
      pendingId?: never;
      proposalId?: never;
      proposalPendingId?: never;
      templateId: string;
    })
  | (BaseProps & {
      proposalId: string;
      proposalPendingId?: never;
      estimateId?: never;
      pendingId?: never;
      templateId?: never;
    })
  | (BaseProps & {
      proposalId?: never;
      proposalPendingId: string;
      estimateId?: never;
      pendingId?: never;
      templateId?: never;
    });

export function EstimateAttachments({
  estimateId,
  pendingId,
  templateId,
  proposalId,
  proposalPendingId,
  attachments,
  onAttachmentsChange,
  isInternal = false,
  readOnly = false,
}: EstimateAttachmentsProps) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Image thumbnails (NOT-475): id → displayable URL. Object URLs for freshly
  // uploaded images give an instant preview; persisted images resolve a
  // presigned download URL below. Users need to see the pictures they add.
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  // Object URLs we minted for fresh-upload previews, keyed by attachment id so
  // each can be revoked when its attachment is deleted/replaced (not just on
  // unmount) — otherwise large blobs leak in a long-lived form. Every allowed
  // type is kept (not just images) so a just-added PDF/video opens instantly
  // too, without a round-trip.
  const objectUrlsRef = useRef<Record<string, string>>({});
  const [preview, setPreview] = useState<{ attachment: Attachment; url: string } | null>(null);
  const queryClient = useQueryClient();

  function revokeObjectUrl(attachmentId: string) {
    const url = objectUrlsRef.current[attachmentId];
    if (url) {
      URL.revokeObjectURL(url);
      delete objectUrlsRef.current[attachmentId];
    }
  }

  // Revoke any object URLs still outstanding when the component unmounts.
  useEffect(
    () => () => {
      Object.values(objectUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  // Resolve presigned URLs for image attachments that have no local blob so they
  // render as thumbnails (edit, read-only detail, and template-copied previews
  // in the create form). Fresh uploads already carry an object URL.
  useEffect(() => {
    const missing = attachments.filter(
      (a) => isRenderableImage(a.mimeType) && !previewUrls[a.id] && downloadUrlPath(a),
    );
    if (missing.length === 0) return;
    let cancelled = false;
    void Promise.all(
      missing.map(async (a) => {
        try {
          const { downloadUrl } = await apiGet<{ downloadUrl: string }>(
            operationsClient,
            downloadUrlPath(a)!,
          );
          return [a.id, downloadUrl] as const;
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const resolved = results.filter((r): r is readonly [string, string] => r !== null);
      if (resolved.length === 0) return;
      setPreviewUrls((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  /**
   * URL to show/open an attachment with. The local blob wins: it is already in
   * memory, needs no round-trip, and is the only source that exists the instant
   * a file finishes uploading in the create flow.
   */
  async function resolveOpenUrl(attachment: Attachment): Promise<string | null> {
    const localUrl = objectUrlsRef.current[attachment.id];
    if (localUrl) return localUrl;
    const path = downloadUrlPath(attachment);
    if (!path) return null;
    const { downloadUrl } = await apiGet<{ downloadUrl: string }>(operationsClient, path);
    return downloadUrl ?? null;
  }

  async function handleOpen(attachment: Attachment) {
    let url: string | null = null;
    try {
      url = await resolveOpenUrl(attachment);
    } catch {
      url = null;
    }
    if (!url) {
      toast.error('Failed to open this file.');
      return;
    }
    if (isDialogPreviewable(attachment.mimeType)) {
      setPreview({ attachment, url });
      return;
    }
    // HEIC and anything else the browser can't paint: hand it to a new tab so
    // the OS/plugin can deal with it rather than showing a broken preview.
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function invalidateEstimatePdf() {
    if (!estimateId) return;
    try {
      await apiDelete(operationsClient, opsEstimateEndpoints.invalidatePdf(estimateId));
    } catch {
      // Non-fatal — stale PDF is a minor UX issue, not a data issue
    }
    void queryClient.invalidateQueries({ queryKey: estimateKeys.detail(estimateId) });
  }

  // Uploads a single file and returns the created Attachment (throws on error).
  async function uploadOne(file: File): Promise<Attachment> {
    let endpoint: string;
    let body: Record<string, unknown>;

    if (templateId) {
      endpoint = estimateTemplateEndpoints.attachmentUploadUrl(templateId);
      body = { filename: file.name, contentType: file.type, fileSize: file.size, isInternal };
    } else if (estimateId) {
      endpoint = opsEstimateEndpoints.attachmentUploadUrl(estimateId);
      body = { filename: file.name, contentType: file.type, fileSize: file.size, isInternal };
    } else if (proposalId) {
      endpoint = opsProposalEndpoints.attachmentUploadUrl(proposalId);
      body = { filename: file.name, contentType: file.type, fileSize: file.size, isInternal };
    } else if (proposalPendingId) {
      endpoint = opsProposalEndpoints.pendingAttachmentUploadUrl();
      body = {
        pendingId: proposalPendingId,
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        isInternal,
      };
    } else {
      endpoint = opsEstimateEndpoints.pendingAttachmentUploadUrl();
      body = {
        pendingId,
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        isInternal,
      };
    }

    const { uploadUrl, attachment } = await apiPost<{
      uploadUrl: string;
      attachment: Attachment;
    }>(operationsClient, endpoint, body);

    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    return attachment;
  }

  // Handles one OR many selected files (#12): validate each, upload all,
  // then apply the new attachments + invalidate queries once.
  async function handleFiles(files: FileList) {
    const selected = Array.from(files);
    if (selected.length === 0) return;

    const valid: File[] = [];
    for (const file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}": type not allowed. Use JPEG, PNG, HEIC, PDF, MP4, or MOV.`);
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(`"${file.name}" exceeds the 50 MB limit.`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;

    setUploading(true);
    const uploaded: Attachment[] = [];
    let failures = 0;
    try {
      for (const file of valid) {
        try {
          const attachment = await uploadOne(file);
          uploaded.push(attachment);

          // Keep the local blob for EVERY freshly uploaded file so it can be
          // opened straight away — including a PDF or a clip, which have no
          // thumbnail but must still be checkable before the document is saved.
          // Revoke any prior blob for this id first (replace). Per-file inside
          // the loop so every file in a multi-select gets its preview, not just
          // the last one.
          revokeObjectUrl(attachment.id);
          const objectUrl = URL.createObjectURL(file);
          objectUrlsRef.current[attachment.id] = objectUrl;
          // Only browser-renderable images become row thumbnails.
          if (isRenderableImage(file.type)) {
            setPreviewUrls((prev) => ({ ...prev, [attachment.id]: objectUrl }));
          }
        } catch (error) {
          failures += 1;
          // NOT-243: surface the real cause (e.g. the S3 PUT status) so a bucket
          // CORS/permission misconfig is diagnosable instead of a generic message.
          const detail = error instanceof Error && error.message ? ` (${error.message})` : '';
          toast.error(`Failed to upload "${file.name}".${detail}`);
        }
      }

      if (uploaded.length > 0) {
        onAttachmentsChange?.([...attachments, ...uploaded]);

        if (templateId) {
          void queryClient.invalidateQueries({
            queryKey: estimateTemplateKeys.attachments(templateId),
          });
        } else if (estimateId) {
          void queryClient.invalidateQueries({
            queryKey: estimateKeys.attachments(estimateId),
          });
          if (!isInternal) void invalidateEstimatePdf();
        } else if (proposalId) {
          void queryClient.invalidateQueries({
            queryKey: proposalKeys.attachments(proposalId),
          });
        }

        if (failures === 0) {
          toast.success(
            uploaded.length === 1 ? 'File attached.' : `${uploaded.length} files attached.`,
          );
        }
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(attachment: Attachment) {
    setDeletingId(attachment.id);
    // Template previews pre-populated in the new-estimate form have entityType='estimate_template'.
    // They don't belong to the pending estimate — skip the API call, remove from local state only.
    const isTemplatePreview =
      (!!pendingId || !!proposalPendingId) &&
      attachment.entityType !== 'estimate' &&
      attachment.entityType !== 'proposal';
    if (!isTemplatePreview) {
      try {
        if (templateId) {
          await apiDelete(
            operationsClient,
            estimateTemplateEndpoints.deleteAttachment(templateId, attachment.id),
          );
        } else if (proposalId || proposalPendingId) {
          const routeId = proposalId ?? proposalPendingId!;
          await apiDelete(
            operationsClient,
            opsProposalEndpoints.deleteAttachment(routeId, attachment.id),
          );
        } else {
          // In pending mode use pendingId as the route's :id
          const routeId = estimateId ?? pendingId!;
          await apiDelete(
            operationsClient,
            opsEstimateEndpoints.deleteAttachment(routeId, attachment.id),
          );
        }
      } catch {
        toast.error('Failed to delete file.');
        setDeletingId(null);
        return;
      }
    }
    setDeletingId(null);
    // Free the preview blob (if any) now that this attachment is gone, and drop
    // any open preview of it — its URL is about to become dead.
    setPreview((current) => (current?.attachment.id === attachment.id ? null : current));
    revokeObjectUrl(attachment.id);
    setPreviewUrls((prev) => {
      if (!(attachment.id in prev)) return prev;
      const next = { ...prev };
      delete next[attachment.id];
      return next;
    });
    onAttachmentsChange?.(attachments.filter((a) => a.id !== attachment.id));
    if (templateId) {
      void queryClient.invalidateQueries({
        queryKey: estimateTemplateKeys.attachments(templateId),
      });
    } else if (estimateId) {
      void queryClient.invalidateQueries({
        queryKey: estimateKeys.attachments(estimateId),
      });
      if (!attachment.isInternal) void invalidateEstimatePdf();
    } else if (proposalId) {
      void queryClient.invalidateQueries({
        queryKey: proposalKeys.attachments(proposalId),
      });
    }
  }

  return (
    <>
      <AttachmentDropzoneList
        attachments={attachments}
        deletingId={deletingId}
        multiple
        onDelete={handleDelete}
        onDownload={handleOpen}
        onFilesSelected={handleFiles}
        previewUrls={previewUrls}
        readOnly={readOnly}
        uploading={uploading}
      />
      <Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="truncate">
              {preview?.attachment.fileName ?? 'Attachment preview'}
            </DialogTitle>
            <DialogDescription>Attachment preview</DialogDescription>
          </DialogHeader>
          <div className="flex min-h-72 items-center justify-center overflow-hidden rounded-lg bg-muted/40">
            {preview && isRenderableImage(preview.attachment.mimeType) ? (
              <img
                src={preview.url}
                alt={preview.attachment.fileName}
                className="max-h-[70vh] max-w-full object-contain"
              />
            ) : preview && isPlayableVideo(preview.attachment.mimeType) ? (
              <video
                src={preview.url}
                controls
                aria-label={preview.attachment.fileName}
                className="max-h-[70vh] max-w-full"
              />
            ) : preview ? (
              <iframe
                src={preview.url}
                title={preview.attachment.fileName}
                className="h-[70vh] w-full"
              />
            ) : null}
          </div>
          <DialogFooter showCloseButton>
            {preview ? (
              <Button asChild variant="outline">
                <a href={preview.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-4" />
                  Open original
                </a>
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
